import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Zap, Key, Plus, Trash2, Copy, Check, LogOut, 
  CreditCard, BarChart3, Settings, AlertCircle, Loader2,
  Eye, EyeOff, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sidebar Component
const Sidebar = ({ activeTab, setActiveTab }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: <BarChart3 className="w-5 h-5" /> },
    { id: 'api-keys', label: 'API Keys', icon: <Key className="w-5 h-5" /> },
    { id: 'subscription', label: 'Subscription', icon: <CreditCard className="w-5 h-5" /> },
    { id: 'settings', label: 'Settings', icon: <Settings className="w-5 h-5" /> },
  ];

  return (
    <aside className="w-64 bg-slate-900/50 border-r border-white/5 flex flex-col" data-testid="dashboard-sidebar">
      {/* Logo */}
      <div className="p-6 border-b border-white/5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <span className="font-['Outfit'] font-bold text-white">Hyper Local</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4">
        <ul className="space-y-1">
          {menuItems.map((item) => (
            <li key={item.id}>
              <button
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === item.id 
                    ? 'bg-indigo-600/20 text-indigo-400' 
                    : 'text-slate-400 hover:bg-white/5 hover:text-white'
                }`}
                data-testid={`nav-${item.id}`}
              >
                {item.icon}
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-white/5">
        <div className="flex items-center gap-3 px-4 py-2">
          <div className="w-8 h-8 bg-indigo-500/20 rounded-full flex items-center justify-center">
            <span className="text-indigo-400 font-semibold text-sm">
              {user?.full_name?.[0] || user?.email?.[0] || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white font-medium truncate">{user?.full_name || 'User'}</p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3 mt-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" />
          Sign out
        </button>
      </div>
    </aside>
  );
};

// Overview Tab
const OverviewTab = ({ subscription }) => {
  const handleDownloadPlugin = () => {
    // Download the WordPress plugin from Google Drive
    window.open('https://drive.google.com/uc?export=download&id=16OuWr8hBowrQXWKmsNOTxJR1OE9CBbRp', '_blank');
    toast.success('Download started!');
  };

  return (
    <div className="space-y-8" data-testid="overview-tab">
      <div>
        <h2 className="font-['Outfit'] text-2xl font-bold text-white mb-2">Dashboard</h2>
        <p className="text-slate-400">Welcome back! Here's your account overview.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="card-base">
          <p className="text-slate-400 text-sm mb-1">Current Plan</p>
          <p className="text-2xl font-['Outfit'] font-bold text-white capitalize">{subscription?.plan_name || 'Free'}</p>
        </div>
        <div className="card-base">
          <p className="text-slate-400 text-sm mb-1">Page Limit</p>
          <p className="text-2xl font-['Outfit'] font-bold text-white">
            {subscription?.page_limit || 5} <span className="text-base text-slate-500">pages</span>
          </p>
        </div>
        <div className="card-base">
          <p className="text-slate-400 text-sm mb-1">Status</p>
          <p className="text-2xl font-['Outfit'] font-bold text-green-400 capitalize">{subscription?.status || 'Active'}</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h3 className="font-['Outfit'] text-lg font-semibold text-white mb-4">Quick Actions</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <button 
            onClick={handleDownloadPlugin}
            className="card-base hover:border-indigo-500/50 cursor-pointer group text-left"
          >
            <h4 className="text-white font-semibold mb-2 group-hover:text-indigo-400 transition-colors">Download WordPress Plugin</h4>
            <p className="text-slate-400 text-sm">Download and install the Hyper Local plugin on your WordPress site.</p>
          </button>
        </div>
      </div>
    </div>
  );
};

// API Keys Tab
const ApiKeysTab = ({ getAuthHeader }) => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyDomain, setNewKeyDomain] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [visibleKeys, setVisibleKeys] = useState({});

  const fetchApiKeys = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/api-keys`, {
        headers: getAuthHeader()
      });
      setApiKeys(response.data);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  const handleCreateKey = async (e) => {
    e.preventDefault();
    if (!newKeyName.trim() || !newKeyDomain.trim()) return;

    setCreating(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/api-keys`,
        { name: newKeyName, domain: newKeyDomain },
        { headers: getAuthHeader() }
      );
      setNewKey(response.data);
      setNewKeyName('');
      setNewKeyDomain('');
      setShowModal(false); // Close the modal after creating
      fetchApiKeys();
      toast.success('API key created!');
    } catch (error) {
      console.error('Failed to create API key:', error);
      toast.error('Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to delete this API key?')) return;

    try {
      await axios.delete(`${API_URL}/api/api-keys/${keyId}`, {
        headers: getAuthHeader()
      });
      fetchApiKeys();
      toast.success('API key deleted');
    } catch (error) {
      console.error('Failed to delete API key:', error);
      toast.error('Failed to delete API key');
    }
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const toggleKeyVisibility = (id) => {
    setVisibleKeys(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="api-keys-tab">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="font-['Outfit'] text-2xl font-bold text-white mb-2">API Keys</h2>
          <p className="text-slate-400">Manage your API keys for the WordPress plugin.</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn-primary flex items-center gap-2 py-2 px-4"
          data-testid="create-api-key-btn"
        >
          <Plus className="w-4 h-4" />
          Create Key
        </button>
      </div>

      {/* New Key Alert */}
      {newKey && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-6" data-testid="new-key-alert">
          <div className="flex items-start gap-3">
            <Check className="w-6 h-6 text-green-500 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-white font-semibold mb-2">API Key Created!</h3>
              <p className="text-slate-400 text-sm mb-4">
                Save this key now. You won't be able to see it again.
              </p>
              <div className="bg-slate-900 rounded-lg p-4 font-mono text-sm break-all flex items-center justify-between gap-4">
                <span className="text-cyan-400">{newKey.key}</span>
                <button
                  onClick={() => copyToClipboard(newKey.key, 'new')}
                  className="text-slate-400 hover:text-white flex-shrink-0"
                >
                  {copiedId === 'new' ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>
              <button
                onClick={() => setNewKey(null)}
                className="mt-4 text-slate-400 hover:text-white text-sm"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Keys List */}
      <div className="space-y-4">
        {apiKeys.length === 0 ? (
          <div className="card-base text-center py-12">
            <Key className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No API keys yet. Create one to get started.</p>
          </div>
        ) : (
          apiKeys.map((key) => (
            <div 
              key={key.id} 
              className="card-base flex items-center justify-between gap-4"
              data-testid={`api-key-item-${key.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3">
                  <p className="text-white font-medium">{key.name}</p>
                  {key.domain && (
                    <span className="text-xs bg-indigo-500/20 text-indigo-400 px-2 py-0.5 rounded">
                      {key.domain}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <code className="text-sm text-cyan-400 font-mono break-all">
                    {visibleKeys[key.id] ? key.key : key.masked_key}
                  </code>
                  <button
                    onClick={() => toggleKeyVisibility(key.id)}
                    className="text-slate-500 hover:text-slate-300 flex-shrink-0"
                    title={visibleKeys[key.id] ? "Hide key" : "Reveal key"}
                  >
                    {visibleKeys[key.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-600 mt-1">
                  Created: {new Date(key.created_at).toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => copyToClipboard(key.key, key.id)}
                  className="p-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                  title="Copy API key"
                >
                  {copiedId === key.id ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => handleDeleteKey(key.id)}
                  className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                  title="Delete key"
                  data-testid={`delete-key-${key.id}`}
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Key Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="card-base max-w-md w-full" data-testid="create-key-modal">
            <h3 className="font-['Outfit'] text-xl font-bold text-white mb-4">Create API Key</h3>
            <form onSubmit={handleCreateKey}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Key Name
                </label>
                <input
                  type="text"
                  required
                  className="input-dark w-full"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="e.g., Production Site"
                  data-testid="key-name-input"
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Website Domain <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  required
                  className="input-dark w-full"
                  value={newKeyDomain}
                  onChange={(e) => setNewKeyDomain(e.target.value)}
                  placeholder="e.g., example.com"
                  data-testid="key-domain-input"
                />
                <p className="text-slate-500 text-xs mt-2">
                  The domain where this API key will be used (without https://).
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setNewKeyName('');
                    setNewKeyDomain('');
                  }}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || !newKeyName.trim() || !newKeyDomain.trim()}
                  className="btn-primary flex-1 disabled:opacity-50"
                  data-testid="confirm-create-key-btn"
                >
                  {creating ? 'Creating...' : 'Create Key'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Subscription Tab
const SubscriptionTab = ({ subscription, getAuthHeader }) => {
  const handleUpgrade = async (planId) => {
    try {
      const response = await axios.post(`${API_URL}/api/checkout/session`, {
        plan_name: planId,
        origin_url: window.location.origin
      }, {
        headers: getAuthHeader()
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout');
    }
  };

  const plans = [
    { id: 'starter', name: 'Starter', price: 49, pages: 50 },
    { id: 'growth', name: 'Growth', price: 99, pages: 200 },
    { id: 'pro', name: 'Pro', price: 199, pages: 500, popular: true },
    { id: 'agency', name: 'Agency', price: 499, pages: 2000 }
  ];

  return (
    <div className="space-y-8" data-testid="subscription-tab">
      <div>
        <h2 className="font-['Outfit'] text-2xl font-bold text-white mb-2">Subscription</h2>
        <p className="text-slate-400">Manage your subscription and billing.</p>
      </div>

      {/* Current Plan */}
      <div className="card-base">
        <h3 className="text-lg font-semibold text-white mb-4">Current Plan</h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-2xl font-['Outfit'] font-bold text-white capitalize">{subscription?.plan_name || 'Free'}</p>
            <p className="text-slate-400 text-sm mt-1">
              {subscription?.monthly_generation_limit || 5} pages/month limit
            </p>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            subscription?.status === 'active' 
              ? 'bg-green-500/20 text-green-400' 
              : 'bg-yellow-500/20 text-yellow-400'
          }`}>
            {subscription?.status || 'Active'}
          </span>
        </div>
      </div>

      {/* Upgrade Plans */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Upgrade Plan</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map((plan) => (
            <div 
              key={plan.id}
              className={`rounded-xl p-6 ${
                plan.popular 
                  ? 'bg-indigo-950/30 border-2 border-indigo-500/50' 
                  : 'card-base'
              }`}
              data-testid={`plan-card-${plan.id}`}
            >
              {plan.popular && (
                <span className="text-xs font-semibold text-indigo-400 uppercase tracking-wide">Popular</span>
              )}
              <h4 className="font-['Outfit'] text-xl font-bold text-white mt-2">{plan.name}</h4>
              <p className="text-3xl font-['Outfit'] font-bold text-white mt-2">
                ${plan.price}<span className="text-base text-slate-500">/mo</span>
              </p>
              <p className="text-slate-400 text-sm mt-2">{plan.pages} pages/month</p>
              <button
                onClick={() => handleUpgrade(plan.id)}
                className={`w-full mt-4 py-2 rounded-lg font-medium transition-colors ${
                  plan.popular 
                    ? 'bg-indigo-600 hover:bg-indigo-500 text-white' 
                    : 'bg-white/5 hover:bg-white/10 text-white border border-white/10'
                }`}
                data-testid={`upgrade-btn-${plan.id}`}
              >
                {subscription?.plan_name === plan.id ? 'Current Plan' : 'Select'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Settings Tab
const SettingsTab = ({ user }) => {
  return (
    <div className="space-y-8" data-testid="settings-tab">
      <div>
        <h2 className="font-['Outfit'] text-2xl font-bold text-white mb-2">Settings</h2>
        <p className="text-slate-400">Manage your account settings.</p>
      </div>

      {/* Profile */}
      <div className="card-base">
        <h3 className="text-lg font-semibold text-white mb-4">Profile</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">Name</label>
            <p className="text-white">{user?.name || user?.full_name || 'Not set'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">Email</label>
            <p className="text-white">{user?.email}</p>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="card-base border-red-500/20">
        <h3 className="text-lg font-semibold text-red-400 mb-4">Danger Zone</h3>
        <p className="text-slate-400 text-sm mb-4">
          Once you delete your account, there is no going back. Please be certain.
        </p>
        <button className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors">
          Delete Account
        </button>
      </div>
    </div>
  );
};

// Main Dashboard Component
const DashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, loading: authLoading, getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [subscription, setSubscription] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  // Check for payment return
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    
    if (sessionId) {
      setCheckingPayment(true);
      // Poll for payment status
      const pollPayment = async (attempts = 0) => {
        if (attempts >= 5) {
          setCheckingPayment(false);
          toast.error('Payment status check timed out');
          return;
        }

        try {
          const response = await axios.get(`${API_URL}/api/checkout/status/${sessionId}`);
          if (response.data.payment_status === 'paid') {
            toast.success('Payment successful! Your subscription is now active.');
            setCheckingPayment(false);
            // Clear URL params
            navigate('/dashboard', { replace: true });
            // Refresh subscription
            fetchSubscription();
          } else if (response.data.status === 'expired') {
            toast.error('Payment session expired');
            setCheckingPayment(false);
          } else {
            setTimeout(() => pollPayment(attempts + 1), 2000);
          }
        } catch (error) {
          console.error('Payment check error:', error);
          setTimeout(() => pollPayment(attempts + 1), 2000);
        }
      };
      pollPayment();
    }
  }, [location, navigate]);

  const fetchSubscription = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/subscription`, {
        headers: getAuthHeader()
      });
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchSubscription();
    }
  }, [isAuthenticated, fetchSubscription]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#020617] flex" data-testid="dashboard-page">
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />
      
      <main className="flex-1 p-8 overflow-auto">
        {/* Payment checking overlay */}
        {checkingPayment && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="card-base text-center">
              <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mx-auto mb-4" />
              <p className="text-white font-medium">Confirming your payment...</p>
              <p className="text-slate-400 text-sm mt-2">This may take a moment</p>
            </div>
          </div>
        )}

        {activeTab === 'overview' && <OverviewTab subscription={subscription} />}
        {activeTab === 'api-keys' && <ApiKeysTab getAuthHeader={getAuthHeader} />}
        {activeTab === 'subscription' && <SubscriptionTab subscription={subscription} getAuthHeader={getAuthHeader} />}
        {activeTab === 'settings' && <SettingsTab user={user} />}
      </main>
    </div>
  );
};

export default DashboardPage;
